import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntil } from 'rxjs';
import { Table, TableColumnReorderEvent, TableLazyLoadEvent } from 'primeng/table';
import { TableState } from 'primeng/api';
import { ToolbarOptions, INFO_BLOCKS, BaseComponent } from '@src/app/components';
import { CaptionService } from '@src/app/services';
import { getObjectCopy } from '@src/app/utils';
import { DictionaryItem, DictionaryService } from '@src/api/v2';

import { Column, ColumnsTemplates, ColumnOptions, TableOptions } from './types';
import { DialogOptions } from './components/dialog/types';
import { RowMenuOptions } from './components/row-menu/types';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<
    ApiDataType,
    FormDataType,
    CreateComponent,
    EditComponent,
    ViewComponent,
    HeaderFilterComponent,
    MassEditComponent,
    AddComponent,
  >
  extends BaseComponent
  implements OnChanges, AfterViewInit
{
  @ViewChild('table') private table?: Table;
  @Output() onInitialized = new EventEmitter<Table>();
  @Output() onSelectionChanged = new EventEmitter<ApiDataType[]>();

  @Input() options?: TableOptions<
    ApiDataType,
    FormDataType,
    CreateComponent,
    EditComponent,
    ViewComponent,
    MassEditComponent,
    AddComponent
  >;
  defaultOptions?: TableOptions<
    ApiDataType,
    FormDataType,
    CreateComponent,
    EditComponent,
    ViewComponent,
    MassEditComponent,
    AddComponent
  >;

  @Output() filterApply = new EventEmitter<void>();
  @Output() filterClear = new EventEmitter<void>();
  @Input() headerFilterTmpRef?: TemplateRef<HeaderFilterComponent>;
  headerFilterVisible = false;
  headerFilterAlwaysVisible = false;

  @Input() addDialogTmpRef?: TemplateRef<AddComponent>;
  addDialogOptions: DialogOptions<AddComponent> = {
    visible: false,
    acceptButton: 'Добавить',
    rejectButton: 'Отмена',
  };
  addData?: ApiDataType;

  @Input() createDialogTmpRef?: TemplateRef<CreateComponent>;
  createDialogOptions: DialogOptions<CreateComponent> = {
    visible: false,
    acceptButton: 'Сохранить',
    rejectButton: 'Отмена',
  };
  copyData?: ApiDataType;

  @Input() editDialogTmpRef?: TemplateRef<EditComponent>;
  editDialogOptions: DialogOptions<EditComponent> = {
    visible: false,
    acceptButton: 'Сохранить',
    rejectButton: 'Отмена',
  };
  editData?: ApiDataType;

  @Input() viewDialogTmpRef?: TemplateRef<ViewComponent>;
  viewDialogOptions: DialogOptions<ViewComponent> = {
    visible: false,
  };
  viewData?: ApiDataType;

  @Input() massEditDialogTmpRef?: TemplateRef<MassEditComponent>;
  massEditDialogOptions: DialogOptions<MassEditComponent> = {
    visible: false,
    acceptButton: 'Сохранить',
    rejectButton: 'Отмена',
  };
  massEditData?: ApiDataType;

  @Input() columnsTemplates?: ColumnsTemplates;
  columnsSettingVisible = false;
  allColumns: Array<Column> = [];
  columnOrderState?: string[];

  dataKey?: string;
  items: any[] = [];
  columns: Column[] = [];
  tableName?: string;

  loading?: boolean;
  set anyLoading(value: boolean) {
    this.loading = value;
    if (this.table) {
      this.table.loading = value;
    }
  }
  get anyLoading() {
    return !!this.loading || !!this.table?.loading;
  }

  selecting = false;
  selectionMode: Table['selectionMode'] = 'multiple';
  selectAll = false;
  selectedItems?: any[] = [];

  toolbarOptions?: ToolbarOptions;
  rowMenuOptions?: RowMenuOptions;
  columnOptions?: ColumnOptions = {
    sorting: false,
    filtering: false,
  };

  paginator = true;
  totalRecords: number = 0;
  currentPageReportTemplate: string = '';
  rowsPerPageOptions: number[] = [10, 15, 25, 50, 100, 200];
  rows: number = this.rowsPerPageOptions[0];
  first: number = 0;

  private stateTable?: TableState;

  @ViewChild('confirmDialog', { read: ViewContainerRef }) private viewRef?: ViewContainerRef;
  private componentRef?: ComponentRef<ConfirmDialogComponent>;

  readonly infoBlockNoFilter = INFO_BLOCKS.noFilter;
  readonly infoBlockNotFound = INFO_BLOCKS.notFound;

  private readonly captionService = inject(CaptionService);

  columnFilterDictionaryValues: Record<string, DictionaryItem[]> = {};
  readonly dictionaryService = inject(DictionaryService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      if (!this.defaultOptions) {
        this.defaultOptions = getObjectCopy(this.options);
      }

      this.toolbarOptions = getObjectCopy(this.options?.toolbarOptions);
      if (this.toolbarOptions?.buttons) {
        this.toolbarOptions.buttons = {
          ...this.toolbarOptions.buttons,
          ...{
            // INFO: по-умолчанию отображаем кнопку для настройки колонок
            columns: this.toolbarOptions?.buttons?.columns !== undefined ? this.toolbarOptions?.buttons?.columns : true,
          },
        };
      }
      this.onSelectionChange(this.selectedItems);

      this.options = {
        ...this.options,
        ...{
          // INFO: по-умолчанию используется queryParams при восстановлении фильтров из URL, при применении фильтра параметры сохраняются в URL
          useQueryParams: this.options?.useQueryParams !== undefined ? this.options?.useQueryParams : true,
          // INFO: по-умолчанию используется storage для хранения состояния таблицы
          useStorage: this.options?.useStorage !== undefined ? this.options?.useStorage : true,
        },
      };

      this.headerFilterAlwaysVisible = this.options?.headerFilterAlwaysVisible ?? false;

      this.rowMenuOptions = { visible: false }; // TODO: '{ visible: false }' заменить на 'this.options?.rowMenuOptions', когда исправят баг с #9099
      this.columnOptions = this.options?.columnOptions;
      this.selecting = this.options?.selecting ?? false;
      this.selectionMode = this.options?.selectionMode ?? 'multiple';

      this.items = this.options?.value ?? [];
      this.allColumns = this.getAllColumns(this.options?.columns, this.options?.excludeColumns);

      this.dataKey = this.options?.dataKey;
      this.tableName = this.options?.tableName;
      if (!this.columnOrderState) {
        this.columns = this.getColumns(this.options?.columns);
      }

      this.paginator = this.options?.paginator ?? true;
      this.first = this.options?.first ?? 0;
      this.rows = this.options?.rows ?? this.rows;
      this.totalRecords = this.options?.totalRecords ?? 0;
      this.currentPageReportTemplate =
        this.options?.currentPageReportTemplate ?? 'Отображено с {first} по {last} из {totalRecords} записей';
      this.setAddDialogOptions(this.options?.addDialogOptions);
      this.setCreateDialogOptions(this.options?.createDialogOptions);
      this.setEditDialogOptions(this.options?.editDialogOptions);
      this.setMassEditDialogOptions(this.options?.massEditDialogOptions);
      this.setViewDialogOptions(this.options?.viewDialogOptions);
      this.selectedItems = this.getSelectionItems(this.selectedItems);

      if (!!this.options?.columnOptions?.columnFilterDictionaryFields?.length) {
        this.options?.columnOptions?.columnFilterDictionaryFields.forEach(fieldName => {
          if (this.tableName) {
            this.dictionaryService
              .dictionaryColumnsValues(this.tableName, fieldName)
              .pipe(takeUntil(this.destroyed$$))
              .subscribe(res => {
                this.columnFilterDictionaryValues[fieldName] = res;
                this.cdr.markForCheck();
              });
          }
        });
      }

      this.loading = this.options?.loading ?? false;
    }

    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    if (this.table && this.stateTable) {
      this.table.first = this.stateTable.first ?? this.first;
      this.table.rows = this.stateTable.rows ?? this.rows;
      this.table.sortField = this.stateTable.sortField;
      this.table.sortOrder = this.stateTable.sortOrder ?? 1;
    }

    this.onInitialized.emit(this.table);
  }

  onClickCheckboxItem(event: MouseEvent) {
    event.stopPropagation(); // TODO: Fix бага выделения Checkbox'а строки
  }

  loadItems(options: TableLazyLoadEvent) {
    this.loading = true;

    this.options?.onLazyLoad && this.options?.onLazyLoad(options);
  }

  stopLoadItems() {
    this.anyLoading = false;

    this.options?.onStopLoad && this.options?.onStopLoad();
  }

  onSelectionChange(value: ApiDataType | ApiDataType[] | null = []) {
    if (!value) {
      value = [];
    }
    if (!Array.isArray(value)) {
      value = [value];
    }

    if (this.toolbarOptions?.visible && this.toolbarOptions.buttons) {
      if (value.length === 1) {
        this.toolbarOptions.buttons.copy = this.options?.toolbarOptions?.buttons?.copy ?? false;
        this.toolbarOptions.buttons.edit = this.options?.toolbarOptions?.buttons?.edit ?? false;
        this.toolbarOptions.buttons.massEdit = false;
        this.toolbarOptions.buttons.delete = this.options?.toolbarOptions?.buttons?.delete ?? false;
        this.toolbarOptions.buttons.restart = this.options?.toolbarOptions?.buttons?.restart ?? false;
        this.toolbarOptions.buttons.resend = this.options?.toolbarOptions?.buttons?.resend ?? false;
        this.toolbarOptions.buttons.stop = this.options?.toolbarOptions?.buttons?.stop ?? false;
        this.toolbarOptions.buttons.turnOn = this.options?.toolbarOptions?.buttons?.turnOn ?? false;
        this.toolbarOptions.buttons.turnOff = this.options?.toolbarOptions?.buttons?.turnOff ?? false;
      } else if (value.length > 0) {
        this.toolbarOptions.buttons.copy = false;
        this.toolbarOptions.buttons.edit = false;
        this.toolbarOptions.buttons.massEdit = this.options?.toolbarOptions?.buttons?.massEdit ?? false;
        this.toolbarOptions.buttons.delete = this.options?.toolbarOptions?.buttons?.delete ?? false;
        this.toolbarOptions.buttons.restart = this.options?.toolbarOptions?.buttons?.restart ?? false;
        this.toolbarOptions.buttons.resend = this.options?.toolbarOptions?.buttons?.resend ?? false;
        this.toolbarOptions.buttons.stop = this.options?.toolbarOptions?.buttons?.stop ?? false;
        this.toolbarOptions.buttons.turnOn = this.options?.toolbarOptions?.buttons?.turnOn ?? false;
        this.toolbarOptions.buttons.turnOff = this.options?.toolbarOptions?.buttons?.turnOff ?? false;
      } else {
        this.toolbarOptions.buttons.copy = false;
        this.toolbarOptions.buttons.edit = false;
        this.toolbarOptions.buttons.massEdit = false;
        this.toolbarOptions.buttons.delete = false;
        this.toolbarOptions.buttons.restart = false;
        this.toolbarOptions.buttons.resend = false;
        this.toolbarOptions.buttons.stop = false;
        this.toolbarOptions.buttons.turnOn = false;
        this.toolbarOptions.buttons.turnOff = false;
      }

      // INFO: для "Настройки модулей" не показывает кнопку удалить, если isCore = true
      const isCore = !!value.find(item => {
        if (typeof item === 'object' && item !== null && 'isCore' in item) {
          return !!item['isCore'];
        }

        return false;
      });
      if (isCore) {
        this.toolbarOptions.buttons.delete = false;
      }

      this.toolbarOptions = {
        ...this.toolbarOptions,
      };
    }

    this.selectAll = value.length === this.totalRecords;

    this.onSelectionChanged.emit(value);
    this.cdr.markForCheck();
  }

  // onSelectAllChange(event: any) {
  //   const checked = event.checked;

  //   if (checked) {
  //     this.customerService.getCustomers().then(res => {
  //       this.selectedItems = res.customers;
  //       this.selectAll = true;
  //     });
  //   } else {
  //     this.selectedItems = [];
  //     this.selectAll = false;
  //   }
  // }

  onFilterApply() {
    this.filterApply.emit();
  }

  onFilterClear() {
    this.filterClear.emit();
  }

  onHeaderFilterVisibleChange(visible: boolean) {
    this.headerFilterVisible = visible;
  }

  openViewDialog(item: ApiDataType) {
    this.viewData = item;
    this.setViewDialogOptions({ visible: true });
  }

  openAddDialog() {
    this.addData = undefined;
    this.setAddDialogOptions({ visible: true });
  }

  openCreateDialog() {
    this.copyData = undefined;
    this.setCreateDialogOptions({ visible: true });
  }

  onCancelAdding() {
    this.openConfirmDialog({ header: 'Отмена добавления' })?.subscribe(res => {
      if (!res) return;

      this.loading = false;
      this.setAddDialogOptions({ visible: false });
    });
  }

  onCancelCreating() {
    this.openConfirmDialog({ header: 'Отмена создания' })?.subscribe(res => {
      if (!res) return;

      this.loading = false;
      this.setCreateDialogOptions({ visible: false });
    });
  }

  async openEditDialog(items: ApiDataType | ApiDataType[] | null = []) {
    if (!items) {
      items = [];
    }
    if (!Array.isArray(items)) {
      items = [items];
    }

    this.editData = items?.[0];

    this.setEditDialogOptions({ visible: true });

    this.cdr.markForCheck();
  }

  onCancelEditing() {
    this.openConfirmDialog({ header: 'Отмена редактирования' })?.subscribe(res => {
      if (!res) return;

      this.loading = false;
      this.setEditDialogOptions({ visible: false });
    });
  }

  openMassEditDialog(items: ApiDataType | ApiDataType[] | null = []) {
    if (!items) {
      items = [];
    }
    if (!Array.isArray(items)) {
      items = [items];
    }

    this.setMassEditDialogOptions({ visible: true });
  }

  onCancelMassEditing() {
    this.openConfirmDialog({ header: 'Отмена массового редактирования' })?.subscribe(res => {
      if (!res) return;

      this.loading = false;
      this.setMassEditDialogOptions({ visible: false });
    });
  }

  onCloseDialog() {
    this.setViewDialogOptions({ visible: false });
    this.setCreateDialogOptions({ visible: false });
    this.setEditDialogOptions({ visible: false });
    this.setMassEditDialogOptions({ visible: false });
  }

  onAdd() {
    if (this.loading) return;

    this.loading = true;

    this.options?.onAdd && this.options?.onAdd();
  }

  onCreate() {
    if (this.loading) return;

    this.loading = true;

    this.options?.onCreate && this.options?.onCreate();
  }

  onEdit(item: any) {
    if (this.loading) return;

    this.loading = true;

    this.options?.onEdit && this.options?.onEdit(item);
  }

  onMassEdit(item: any) {
    if (this.loading) return;

    this.loading = true;

    this.options?.onMassEdit && this.options?.onMassEdit(item);
  }

  openCopyDialog(items?: any[]) {
    if (!items || !items.length) return;

    this.copyData = items[0];
    this.setCreateDialogOptions({ visible: true });
  }

  onDelete(items?: any[]) {
    if (!items || !items.length || this.loading) return;

    this.openConfirmDialog({ header: 'Удаление' })?.subscribe(res => {
      if (!res) return;

      this.loading = true;

      this.options?.onDelete && this.options?.onDelete(items);
    });
  }

  onRestart(items?: any[]) {
    if (!items || !items.length || this.loading) return;

    this.loading = true;

    this.options?.onRestart && this.options?.onRestart(items);
  }

  onResend(items?: any[]) {
    if (!items || !items.length || this.loading) return;

    this.openConfirmDialog({ header: 'Переотправка' })?.subscribe(res => {
      if (!res) return;

      this.loading = true;

      this.options?.onResend && this.options?.onResend(items);
    });
  }

  onTurnOff(items?: any[]) {
    if (!items || !items.length || this.loading) return;

    this.loading = true;

    this.options?.onTurnOff && this.options?.onTurnOff(items);
  }

  onTurnOn(items?: any[]) {
    if (!items || !items.length || this.loading) return;

    this.loading = true;

    this.options?.onTurnOn && this.options?.onTurnOn(items);
  }

  onStop(items?: any[]) {
    if (!items || !items.length || this.loading) return;

    this.loading = true;

    this.options?.onStop && this.options?.onStop(items);
  }

  onExportCSV() {
    // TODO: выдает пустой файл. разобраться. попробовать обновить библиотеку до актуальной версии
    this.table?.exportCSV();
  }

  onColumnsSettingToggle() {
    this.columnsSettingVisible = !this.columnsSettingVisible;
  }

  onClearCacheTable(tableName?: string) {
    if (!tableName || !this.table) return;

    this.table.clearState();

    this.stateTable = undefined;
    this.columnOrderState = undefined;
    this.columns = this.getColumns(this.defaultOptions?.columns);
    this.rows = this.defaultOptions?.rows ?? this.rowsPerPageOptions[0];
    this.first = 0;
    this.selectedItems = [];

    if (this.options) {
      this.options.first = this.first;
      this.options.sortField = this.defaultOptions?.sortField;
      this.options.sortOrder = this.defaultOptions?.sortOrder;
    }

    this.table.first = this.first;
    this.table.rows = this.rows;
    this.table.sortField = this.defaultOptions?.sortField;
    this.table.sortOrder = this.defaultOptions?.sortOrder ?? 1;

    this.onSelectionChange([]);
    this.loadItems(this.table.createLazyLoadMetadata());

    this.cdr.markForCheck();
  }

  stateRestore(state: TableState) {
    this.stateTable = state;

    this.columnOrderState = state.columnOrder;
    this.columns = this.getColumns(this.columnOrderState);
    this.first = state.first ?? this.options?.first ?? this.first;
    this.rows = state.rows ?? this.options?.rows ?? this.rows;
    // При позднем восстановлении неполного состояния PrimeNG может сбросить пагинацию в undefined.
    if (this.table) {
      this.table.first = this.first;
      this.table.rows = this.rows;
    }
    if (this.options) {
      this.options.first = this.first;
      this.options.rows = this.rows;
      this.options.sortField = state.sortField;
      this.options.sortOrder = state.sortOrder;
    }
    this.cdr.markForCheck();
  }

  stateSave() {
    if (this.table) {
      this.table.saveState();
    }
    this.cdr.markForCheck();
  }

  getColumnFilterType(fieldName: string) {
    let fieldType = 'text';

    const columnFilterTypesFields = this.columnOptions?.columnFilterTypesFields;
    if (columnFilterTypesFields && fieldName in columnFilterTypesFields) {
      fieldType = columnFilterTypesFields[fieldName];
    }

    return fieldType;
  }

  private setAddDialogOptions(options?: DialogOptions<any>) {
    if (!options) return;

    this.addDialogOptions = { ...this.addDialogOptions, ...options };
  }

  private setCreateDialogOptions(options?: DialogOptions<any>) {
    if (!options) return;

    this.createDialogOptions = { ...this.createDialogOptions, ...options };
  }

  private setEditDialogOptions(options?: DialogOptions<any>) {
    if (!options) return;

    this.editDialogOptions = { ...this.editDialogOptions, ...options };
  }

  private setMassEditDialogOptions(options?: DialogOptions<any>) {
    if (!options) return;

    this.massEditDialogOptions = { ...this.massEditDialogOptions, ...options };
  }

  private setViewDialogOptions(options?: DialogOptions<any>) {
    if (!options) return;

    this.viewDialogOptions = { ...this.viewDialogOptions, ...options };
  }

  private openConfirmDialog(options: DialogOptions<any>) {
    this.viewRef?.clear();
    this.componentRef = this.viewRef?.createComponent(ConfirmDialogComponent);
    if (this.componentRef) {
      this.componentRef.instance.open(options);
    }

    return this.componentRef?.instance.result;
  }

  private closeConfirmDialog() {
    this.viewRef?.clear();
  }

  setColumns(event: TableColumnReorderEvent) {
    this.columns = event.columns ?? [];
    this.cdr.markForCheck();
  }

  private getAllColumns(columns: string[] = [], excludeColumns: string[] = []) {
    const allColumns = this.items.length ? Object.keys(this.items[0]) : columns;
    return allColumns
      .filter(column => !excludeColumns.includes(column))
      .map(column => {
        return { header: this.captionService.getCaption(column, this.tableName), field: column } as Column;
      });
  }
  private getColumns(columns: string[] = []) {
    let filterColumns: string[];
    const allColumns = this.items.length ? Object.keys(this.items[0]) : columns;
    this.allColumns = allColumns.map(column => {
      return { header: this.captionService.getCaption(column, this.tableName), field: column } as Column;
    });

    if (columns.length) {
      filterColumns = columns.filter(col => allColumns.includes(col));
    } else {
      filterColumns = allColumns;
    }
    return filterColumns.map(column => {
      return { header: this.captionService.getCaption(column, this.tableName), field: column } as Column;
    });
  }

  private getSelectionItems(selectedItems?: any[]) {
    if (!Array.isArray(selectedItems)) {
      selectedItems = [selectedItems];
    }

    return selectedItems?.map(selectedItem => {
      if (this.dataKey) {
        const actualItem = this.items.find(item => item[this.dataKey!] === selectedItem[this.dataKey!]);
        if (actualItem) {
          return actualItem;
        }
      }
      return selectedItem;
    });
  }
}
